import React from "react";
import { Heart, Search, Calendar, Users, Zap, Award, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import RouteRow from "../Includes/RouteRow";
import useSavedLoads from "../hooks/useSavedLoads";

export default function SavedLoads() {
  const { savedLoads, toggleSave, autoSave, setAutoSave } = useSavedLoads();

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Saved Loads</h1>
          <p className="text-sm text-gray-500 mt-1">Keep track of interesting routes before applying.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoSave(!autoSave)}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-bold transition-colors ${
              autoSave ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-white border-gray-200 text-gray-500"
            }`}
            title="Automatically save Urgent and Priority Match loads from the board"
          >
            <Heart className={`w-4 h-4 ${autoSave ? "fill-rose-500 text-rose-500" : "text-gray-400"}`} />
            Auto-save {autoSave ? "on" : "off"}
          </button>
          <Link to="/loadboard" className="flex-shrink-0">
            <button className="w-full sm:w-auto bg-[#1a2744] hover:bg-[#1a2744]/90 text-white font-bold px-5 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm">
              <Search className="w-4 h-4" /> Browse Load Board
            </button>
          </Link>
        </div>
      </div>

      {savedLoads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center border-2 border-dashed border-gray-200 rounded-2xl bg-white">
          <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mb-4">
            <Heart className="w-8 h-8 text-rose-300" />
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-2">No saved loads yet</h3>
          <p className="text-sm text-gray-500 max-w-sm mb-6 leading-relaxed">
            Tap the heart icon on any load in the Load Board to save it here — or turn on
            auto-save above and Urgent / Priority Match loads will save themselves.
          </p>
          <Link to="/loadboard">
            <button className="bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-bold px-5 py-2.5 rounded-xl transition-colors text-sm">
              Explore Routes
            </button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {savedLoads.map((load) => (
            <div key={load.pk ?? load.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">#{load.id}</span>
                  {load.status === "Urgent" && (
                    <span className="bg-red-50 text-red-600 text-[10px] font-black px-2 py-1 rounded-md border border-red-100 flex items-center gap-1">
                      <Zap className="w-3 h-3" /> Urgent
                    </span>
                  )}
                  {load.auto_saved && (
                    <span className="bg-rose-50 text-rose-600 text-[10px] font-black px-2 py-1 rounded-md border border-rose-100 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Auto-saved
                    </span>
                  )}
                </div>
                <button
                  onClick={() => toggleSave(load)}
                  aria-label="Remove from saved loads"
                  className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-rose-50 hover:border-rose-200 transition-colors"
                >
                  <Heart className="w-4 h-4 fill-rose-500 text-rose-500" />
                </button>
              </div>

              <h3 className="font-black text-gray-900 text-base -mt-2">{load.commodity || "Oversize Load"}</h3>

              <RouteRow from={load.from} to={load.to} compact />

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Pay range</p>
                  <p className="text-lg font-black text-emerald-600">{load.pay}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1 justify-end">
                    <Calendar className="w-3 h-3" /> {load.date || "TBD"}
                  </p>
                  <p className="text-xs font-bold text-gray-500 flex items-center gap-1 justify-end mt-1">
                    <Users className="w-3 h-3" /> {load.escorts || 0} escort{load.escorts === 1 ? "" : "s"}
                  </p>
                </div>
              </div>

              <Link to="/loadboard">
                <button className="w-full mt-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 font-bold py-2.5 rounded-xl text-sm transition-colors flex items-center justify-center gap-1.5">
                  <Award className="w-4 h-4" /> View on Load Board
                </button>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
